import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { requestServerGet, requestServerPost } from "../../actions"; // Adjust the import path as necessary
import { connect } from "react-redux";
import { Checkbox, Modal, Tooltip, Grid } from "antd";
import { InfoCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { THEME_PALLETE } from "../../Theme";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const AssignToPlaylistsView = (props) => {
  const { visible, onCancel, onAssign, data } = props;
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [playlistsData, setPlaylistsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).assignToPlaylists;

  const screens = Grid.useBreakpoint();
  const isMobile = screens.sm === false;

  const [durationType, setDurationType] = useState("default");
  const [customDuration, setCustomDuration] = useState(30);

  // Fetch playlists data when component mounts or data changes
  useEffect(() => {
    if (visible && data && data.FileID && data.BrandID) {
      fetchPlaylistsData();
    }
  }, [visible, data, props.selectedBranchData]);

  const handleDurationTypeChange = (e) => {
    setDurationType(e.target.value);
  };

  const handleCustomDurationChange = (e) => {
    // const value = parseInt(e.target.value) || 0;
    setCustomDuration(parseInt(e.target.value));
  };

  const fetchPlaylistsData = async () => {
    setLoading(true);
    setError(null);

    const { requestServerGet, usertoken } = props;

    try {
      // Fetch both get_playlistmedia (for FileDatas) and get_playlists (for AccessBranches) in parallel
      const [playlistMediaResponse, playlistsResponse] = await Promise.all([
        new Promise((resolve) =>
          requestServerGet(
            "get_playlistmedia",
            { filetypeid: data.FileID, brandID: data.BrandID },
            {},
            resolve,
            usertoken,
          ),
        ),
        new Promise((resolve) =>
          requestServerGet(
            "get_playlists",
            { brandid: data.BrandID },
            {},
            resolve,
            usertoken,
          ),
        ),
      ]);

      // Build lookup map: PlaylistID → AccessBranches
      const accessBranchesMap = {};
      if (playlistsResponse?.data && Array.isArray(playlistsResponse.data)) {
        playlistsResponse.data.forEach((pl) => {
          accessBranchesMap[String(pl.PlaylistID)] = pl.AccessBranches ?? "";
        });
      }

      console.log("Playlist media response:", playlistMediaResponse);
      // Merge AccessBranches into get_playlistmedia results
      const mergedData = {
        ...playlistMediaResponse.data,
        data: (playlistMediaResponse.data?.data || []).map((pl) => ({
          ...pl,
          // AccessBranches: accessBranchesMap[String(pl.PlaylistID)] ?? "",
        })),
      };

      console.log("Playlists data fetched:", mergedData);
      setPlaylistsData(mergedData);
    } catch (err) {
      console.error("Error fetching playlists:", err);
      setError(t.errorLoad);

      // Fallback to sample data for demo purposes
      const sampleData = {
        status: "success",
        data: [
          {
            PlaylistID: 116,
            PlaylistName: "pl1",
            BrandID: 13,
            FileDatas: [
              {
                FileID: "",
                MediaCount: 2,
              },
              {
                FileID: 2229,
                MediaCount: 2,
              },
              {
                FileID: 25204,
                MediaCount: 1,
              },
              {
                FileID: 27022,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 479,
            PlaylistName: "TestWidget",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 25204,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 1404,
            PlaylistName: "pl02",
            BrandID: 13,
            FileDatas: [
              {
                FileID: "",
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 1997,
            PlaylistName: "TestPL",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 2229,
                MediaCount: 1,
              },
              {
                FileID: 5352,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 2054,
            PlaylistName: "123456789",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 2229,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 2055,
            PlaylistName: "123456789",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 2229,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 2056,
            PlaylistName: "pl12313456155",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 2229,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 2058,
            PlaylistName: "pl12313456155",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 2229,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 2160,
            PlaylistName: "DOOH_PL",
            BrandID: 13,
            FileDatas: [
              {
                FileID: 25204,
                MediaCount: 1,
              },
            ],
          },
          {
            PlaylistID: 2248,
            PlaylistName: "testslot2",
            BrandID: 13,
            FileDatas: [
              {
                FileID: "",
                MediaCount: 1,
              },
            ],
          },
        ],
      };
      setPlaylistsData(sampleData);
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedPlaylists([]);
      setFilter("all");
      setSearchTerm("");
      setError(null);
    }
  }, [visible]);

  // Get count of selected file in a playlist
  const getFileCount = (playlist) => {
    if (!data || !data.FileID) return 0;
    const fileData = playlist.FileDatas.find(
      (fd) => fd.FileID.toString() === data.FileID.toString(),
    );
    return fileData ? fileData.MediaCount : 0;
  };

  // Check if playlist contains the selected file
  const playlistContainsFile = (playlist) => {
    return getFileCount(playlist) > 0;
  };

  // Parse AccessBranches string into an array of branch ID strings.
  // Returns null when access is unrestricted (all branches).
  const parseAccessBranches = (accessBranches) => {
    if (
      !accessBranches ||
      accessBranches === "all" ||
      accessBranches === "null"
    )
      return null;
    return accessBranches
      .split(",")
      .map((id) => String(id).trim())
      .filter(Boolean);
  };

  // Filter playlists based on current filter and search
  const getFilteredPlaylists = () => {
    if (!playlistsData || !playlistsData.data) return [];

    const { selectedBrandData, selectedBranchData } = props;
    const isSharingMode2 =
      selectedBrandData &&
      (selectedBrandData.ContentSharingMode == 2 ||
        selectedBrandData.ContentSharingMode === "2");
    const isBranchLevel = isSharingMode2 && selectedBranchData != null;
    const isBrandLevel = isSharingMode2 && selectedBranchData == null;

    console.log(
      "playlist names",
      playlistsData.data.map((pl) => pl.PlaylistName),
      "selected branch",
      selectedBranchData?.BranchName,
      "isBranchLevel",
      isBranchLevel,
      "isBrandLevel",
      isBrandLevel,
    );
    let filtered = playlistsData.data.filter((playlist) => {
      console.log(
        "checking playlist " + playlist.PlaylistName,
        "access branches",
        playlist.AccessBranches,
      );
      console.log("isBranchLevel", isBranchLevel, "isBrandLevel", isBrandLevel);
      // --- Mode 2 branch-level: only show playlists accessible by the current branch ---
      if (isBranchLevel) {
        const branches = parseAccessBranches(playlist.AccessBranches);
        console.log("playlist access branches", branches);
        if (branches !== null) {
          console.log(
            "playlist branches",
            branches,
            "selected branch",
            selectedBranchData.BranchID,
          );
          // restricted — current branch must be in the list
          if (!branches.includes(String(selectedBranchData.BranchID))) {
            console.log(
              "playlist " +
                playlist.PlaylistName +
                " is NOT accessible by branch " +
                selectedBranchData.BranchName,
            );
            return false;
          } else {
            console.log(
              "playlist " +
                playlist.PlaylistName +
                " is accessible by branch " +
                selectedBranchData.BranchName,
            );
          }
        }
        // else: all-branches playlist → always visible at branch level
      }

      // --- Mode 2 brand-level: playlist's access branches must be a subset of media's access branches ---
      if (isBrandLevel) {
        const mediaBranches = parseAccessBranches(
          data ? data.AccessBranches : null,
        );
        const playlistBranches = parseAccessBranches(playlist.AccessBranches);

        if (mediaBranches === null) {
          // Media is shared to all → any playlist is compatible
        } else if (playlistBranches === null) {
          // Playlist is shared to all, but media is restricted → not a subset
          return false;
        } else {
          // Both restricted: every playlist branch must be in media's branches
          const isSubset = playlistBranches.every((bid) =>
            mediaBranches.includes(bid),
          );
          if (!isSubset) return false;
        }
      }

      // Search filter
      if (searchTerm) {
        const matchesSearch = playlist.PlaylistName.toLowerCase().includes(
          searchTerm.toLowerCase(),
        );
        if (!matchesSearch) return false;
      }

      // File presence filter
      if (filter === "contains") {
        return playlistContainsFile(playlist);
      } else if (filter === "without") {
        return !playlistContainsFile(playlist);
      }

      return true; // 'all' filter
    });

    return filtered;
  };

  // Handle playlist selection
  const handlePlaylistToggle = (playlistId) => {
    const isSelected = selectedPlaylists.includes(playlistId);

    if (isSelected) {
      setSelectedPlaylists(selectedPlaylists.filter((id) => id !== playlistId));
    } else {
      setSelectedPlaylists([...selectedPlaylists, playlistId]);
    }
  };

  // Handle assign button click
  const handleAssign = () => {
    if (onAssign && selectedPlaylists.length > 0) {
      const { requestServerPost, usertoken } = props;

      const params = new FormData();
      params.append("fileid", data.FileID);
      params.append("playlistids", selectedPlaylists.join(","));

      if (durationType !== "default") {
        if (!customDuration) {
          Modal.error({
            content: t.errorCustomDuration,
            onOk: () => {},
          });
          return;
        }
        params.append("durations", customDuration);
      }

      requestServerPost(
        "update_mediaplaylist",
        params,
        {},
        (response) => {
          Modal.success({
            content: t.successAdded,
            onOk: () => {
              if (visible && data && data.FileID && data.BrandID) {
                fetchPlaylistsData();
              }
              setSelectedPlaylists([]);
            },
          });
        },
        usertoken,
      );
      console.log("Selected Playlists:", selectedPlaylists);
      // onAssign(selectedPlaylists);
    }
  };

  if (!visible) return null;

  const filteredPlaylists = getFilteredPlaylists();

  const styles = {
    container: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: isMobile ? "8px" : "12px",
      fontSize: "14px",
      justifyContent: "start",
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
    radioGroup: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    radioOption: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      cursor: "pointer",
    },
    numberInput: {
      width: "60px",
      padding: "2px 6px",
      border: "1px solid #ccc",
      borderRadius: "3px",
      fontSize: "14px",
    },
  };

  const modalStyle = {
    // position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const contentStyle = {
    backgroundColor: "white",
    borderRadius: "8px",
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto",
    height: isMobile ? "80vh" : "90%",
    maxHeight: isMobile ? "80vh" : "600px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  };

  const headerStyle = {
    padding: isMobile ? "12px 14px" : "16px 20px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const titleStyle = {
    margin: 0,
    fontSize: isMobile ? 16 : 18,
    fontWeight: "500",
  };

  const closeButtonStyle = {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
  };

  const filtersStyle = {
    padding: isMobile ? "12px 14px" : "16px 20px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    gap: isMobile ? "8px" : "12px",
    flexWrap: "wrap",
    alignItems: "center",
  };

  const searchInputStyle = {
    flex: 1,
    minWidth: "150px",
    padding: "6px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: "4px",
    fontSize: "14px",
  };

  const filterButtonStyle = (isActive) => ({
    padding: "6px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: "4px",
    background: isActive ? "#1890ff" : "white",
    color: isActive ? "white" : "#595959",
    cursor: "pointer",
    fontSize: "12px",
  });

  const listStyle = {
    flex: 1,
    overflow: "auto",
    padding: "8px 0",
  };

  const listItemStyle = (isSelected, hasFile) => ({
    padding: isMobile ? "10px 14px" : "12px 20px",
    borderBottom: "1px solid #f5f5f5",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: isSelected ? "#e6f7ff" : hasFile ? "#fff7e6" : "white",
    borderLeft: hasFile ? "3px solid #fa8c16" : "3px solid transparent",
  });

  const playlistNameStyle = {
    fontSize: "14px",
    fontWeight: "500",
  };

  const fileCountStyle = {
    fontSize: "12px",
    color: "#fa8c16",
    fontWeight: "500",
  };

  const footerStyle = {
    padding: isMobile ? "12px 14px" : "16px 20px",
    borderTop: "1px solid #f0f0f0",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "stretch" : "center",
    justifyContent: "space-between",
    gap: isMobile ? "12px" : "8px",
  };

  const footerButtonsStyle = {
    display: "flex",
    gap: "8px",
    alignItems: "stretch",
  };

  const buttonStyle = (isPrimary) => ({
    padding: "8px 16px",
    border: isPrimary ? "none" : "1px solid #d9d9d9",
    borderRadius: "4px",
    background: isPrimary ? "#1890ff" : "white",
    color: isPrimary ? "white" : "#595959",
    cursor: "pointer",
    fontSize: "14px",
    flex: isMobile ? 1 : "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    lineHeight: 1.3,
    minHeight: isMobile ? 40 : undefined,
  });

  const loadingStyle = {
    padding: "40px 20px",
    textAlign: "center",
    color: "#666",
  };

  const errorStyle = {
    padding: "40px 20px",
    textAlign: "center",
    color: "#ff4d4f",
  };

  const onCheckAllChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const allPlaylistIds = filteredPlaylists.map(
        (playlist) => playlist.PlaylistID,
      );
      setSelectedPlaylists(allPlaylistIds);
    } else {
      setSelectedPlaylists([]);
    }
  };

  return (
    // <div style={modalStyle} onClick={onCancel}>
    <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>
          {t.title}
          {data && data.FileID && (
            <span
              style={{
                fontSize: "12px",
                fontWeight: "normal",
                color: "#666",
                marginLeft: "8px",
              }}
            >
              {t.mediaLabel(data.FileName)}
            </span>
          )}
        </h3>
        {/* <button style={closeButtonStyle} onClick={onCancel}>×</button> */}
      </div>

      {/* Loading State */}
      {loading && (
        <div style={loadingStyle}>
          <div>{t.loading}</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div style={errorStyle}>
          <div>{error}</div>
          <button
            style={{ ...buttonStyle(true), marginTop: "8px" }}
            onClick={fetchPlaylistsData}
          >
            {t.retry}
          </button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && playlistsData && (
        <>
          {/* Filters */}
          <div style={filtersStyle}>
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={searchInputStyle}
            />
            <button
              style={filterButtonStyle(filter === "all")}
              onClick={() => setFilter("all")}
            >
              {t.filterAll}
            </button>
            <button
              style={filterButtonStyle(filter === "contains")}
              onClick={() => setFilter("contains")}
            >
              {t.filterContains}
            </button>
            <button
              style={filterButtonStyle(filter === "without")}
              onClick={() => setFilter("without")}
            >
              {t.filterWithout}
            </button>
            <Checkbox
              indeterminate={
                selectedPlaylists.length > 0 &&
                selectedPlaylists.length < filteredPlaylists.length
              }
              onChange={onCheckAllChange}
              checked={selectedPlaylists.length === filteredPlaylists.length}
            >
              {t.checkAll}
            </Checkbox>
          </div>

          {/* Playlist List */}
          <div style={listStyle}>
            {filteredPlaylists.map((playlist) => {
              const isSelected = selectedPlaylists.includes(
                playlist.PlaylistID,
              );
              const fileCount = getFileCount(playlist);
              const hasFile = fileCount > 0;

              return (
                <div
                  key={playlist.PlaylistID}
                  style={listItemStyle(isSelected, hasFile)}
                  onClick={() => handlePlaylistToggle(playlist.PlaylistID)}
                >
                  <div>
                    <div style={playlistNameStyle}>{playlist.PlaylistName}</div>
                    {hasFile && (
                      <div style={fileCountStyle}>
                        {t.mediaAppears(fileCount)}
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={{ pointerEvents: "none" }}
                    />
                  </div>
                </div>
              );
            })}
            {filteredPlaylists.length === 0 && !loading && (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                {t.noPlaylists}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <div style={styles.container}>
              <span>{t.duration}</span>
              <div style={styles.radioGroup}>
                <label style={styles.radioOption}>
                  <input
                    type="radio"
                    name="durationType"
                    value="default"
                    checked={durationType === "default"}
                    onChange={handleDurationTypeChange}
                  />
                  <span>
                    {t.durationDefault}
                    <Tooltip title={t.durationTooltip}>
                      {" "}
                      <InfoCircleOutlined
                        style={{ color: THEME_PALLETE.blue }}
                      />
                    </Tooltip>
                  </span>
                </label>
                <label style={styles.radioOption}>
                  <input
                    type="radio"
                    name="durationType"
                    value="custom"
                    checked={durationType === "custom"}
                    onChange={handleDurationTypeChange}
                  />
                  <span>{t.durationCustom}</span>
                </label>
              </div>

              {durationType === "custom" && (
                <input
                  type="number"
                  min="1"
                  max="3600"
                  value={customDuration}
                  onChange={handleCustomDurationChange}
                  placeholder="sec"
                  style={styles.numberInput}
                />
              )}
            </div>
            <div style={footerButtonsStyle}>
              <button style={buttonStyle(false)} onClick={onCancel}>
                {t.cancel}
              </button>
              <button
                style={buttonStyle(selectedPlaylists.length !== 0)}
                onClick={handleAssign}
                disabled={selectedPlaylists.length === 0}
              >
                {t.addToPlaylists(selectedPlaylists.length)}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
    // </div>
  );
};

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    globalSearch: state.globalSearch,
    selectedPage: state.selectedPage,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
  };
};

export default connect(mapStateToProps, {
  requestServerGet,
  requestServerPost,
})(AssignToPlaylistsView);
